import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import SEOHead from '@/components/feature/SEOHead';
import { comics, categories, getHotComics, getNewComics, getRecommendedComics } from '@/mocks/comics';

export default function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const heroComics = getRecommendedComics().slice(0, 5);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroComics.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroComics.length]);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '51漫画',
    url: 'https://www.51manga.com/',
    description: '51漫画在线观看页面，提供免费漫画全集在线阅读、下拉式阅读、实时阅读，无需下载即看热门漫画大全，支持网页观看与在线漫画。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.51manga.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '51漫画',
    url: 'https://www.51manga.com/',
    description: '51漫画 - 免费在线漫画阅读平台',
  };

  return (
    <>
      <SEOHead
        title="51漫画 | 51漫画在线观看与在线漫画在线看漫画 - 免费漫画全集在线网页观看下拉式漫画大全实时阅读"
        description="51漫画在线观看页面，提供免费漫画全集在线阅读、下拉式阅读、实时阅读，无需下载即看热门漫画大全，支持网页观看与在线漫画。海量国产漫画、日漫、韩漫免费看，每日更新，流畅阅读体验。"
        keywords="51漫画在线观看,51漫画在线,51漫画在线看,在线阅读,免费漫画,下拉式漫画,漫画大全,实时阅读,网页观看,在线漫画"
        canonicalUrl="https://www.51manga.com/"
        schema={websiteSchema}
      />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            {heroComics.map((comic, index) => (
              <div
                key={comic.id}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/70"></div>
              </div>
            ))}

            <div className="absolute bottom-0 left-0 right-0 z-10">
              <div className="max-w-7xl mx-auto px-4 md:px-6 pb-8 md:pb-12">
                <div className="flex items-end gap-6">
                  <div className="hidden md:block">
                    <img
                      src={heroComics[currentSlide]?.cover}
                      alt={heroComics[currentSlide]?.title}
                      className="w-[140px] h-[189px] rounded-lg object-cover object-top border-2 border-white/30"
                    />
                  </div>
                  <div className="flex-1">
                    <h1 className="text-xl md:text-3xl font-heading font-bold text-white mb-2">
                      {heroComics[currentSlide]?.title}
                    </h1>
                    <p className="text-white/70 text-sm line-clamp-2 max-w-2xl mb-4">
                      {heroComics[currentSlide]?.description}
                    </p>
                    <div className="flex items-center gap-3 flex-wrap">
                      <Link
                        to={`/article/${heroComics[currentSlide]?.id}`}
                        className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-play-circle-line"></i>
                        开始阅读
                      </Link>
                      <Link
                        to="/category/all"
                        className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer"
                      >
                        查看更多
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute bottom-4 right-4 md:right-6 flex items-center gap-1.5 z-10">
              {heroComics.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                    index === currentSlide
                      ? 'bg-primary-500 w-6'
                      : 'bg-white/50 hover:bg-white/80'
                  }`}
                  aria-label={`切换到第${index + 1}张`}
                />
              ))}
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">
                分类浏览
              </h2>
              <Link
                to="/category/all"
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-11 gap-2 md:gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center justify-center px-3 py-3 bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-500 transition-all cursor-pointer whitespace-nowrap"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-4 md:px-6 pb-10 md:pb-14">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">
                <i className="ri-fire-line text-primary-500 mr-2"></i>
                热门推荐
              </h2>
              <Link
                to="/category/all"
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                更多 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {getHotComics().slice(0, 6).map((comic) => (
                <ComicCard key={comic.id} comic={comic} size="medium" />
              ))}
            </div>
          </section>

          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6">
                <i className="ri-star-line text-accent-500 mr-2"></i>
                编辑推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {getRecommendedComics().map((comic) => (
                  <ComicCard key={comic.id} comic={comic} size="medium" />
                ))}
              </div>
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">
                <i className="ri-timer-line text-secondary-500 mr-2"></i>
                最新上架
              </h2>
              <Link
                to="/category/all"
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                更多 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {getNewComics().map((comic) => (
                <ComicCard key={comic.id} comic={comic} size="medium" />
              ))}
            </div>
          </section>

          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6">
                最近更新
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {comics.slice(0, 10).map((comic) => {
                  const latestChapter = comic.chapters[comic.chapters.length - 1];
                  return (
                    <Link
                      key={comic.id}
                      to={`/article/${comic.id}`}
                      className="flex items-center gap-4 p-3 bg-background-50 hover:bg-background-200/50 rounded-lg border border-background-200/70 transition-colors cursor-pointer group"
                    >
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-12 h-16 rounded object-cover object-top flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate whitespace-nowrap">
                          {comic.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-primary-500 font-medium whitespace-nowrap">
                          {latestChapter?.title}
                        </p>
                        <p className="text-[10px] text-foreground-400 mt-0.5">
                          {latestChapter?.updatedAt}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">
                全部漫画
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4 md:gap-5">
              {comics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} size="small" />
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}